import { BottomAction } from '@euler/app/components/BottomAction';
import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { useTaskHeaderMetrics } from '@euler/app/flows/task/functions/useTaskHeaderMetrics';
import { ReworkRecordBottomSheet } from '@euler/app/flows/task/pages/delivery-check/rework-record/ReworkRecordBottomSheet';
import { WiredTaskDetailPageContainer } from '@euler/app/flows/task/pages/detail/components/DetailPageContainer';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { showAlert } from '@euler/utils';
import { memo, useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { useDeliveryCheck } from '../functions/useDeliveryCheck';
import { useDeliveryCheckMediaController } from '../functions/useDeliveryCheckMediaController';
import { useDeliveryCheckScrollView } from '../functions/useDeliveryCheckScrollView';
import { useDeliveryCheckUserPicker } from '../functions/useDeliveryCheckUserPicker';
import { useDeliveryCheckWizard } from '../functions/useDeliveryCheckWizard';
import { InvalidDeliveryCheckItemHandler } from '../functions/util';
import { DeliveryCheckCell } from './DeliveryCheckCell';
import { DeliveryCheckUserBottomSheetModal } from './DeliveryCheckUserBottomSheetModal';

export const WiredDeliveryCheckScreenContent = memo(() => {
  const pageMetrics = useTaskHeaderMetrics();
  const { rpx } = useResponsiveContext();
  const { windowDimensions, safeAreaInsets } = useSystemMetrics();

  const {
    scrollOffset,
    scrollViewRef,
    scrollToItemAnimated,
    onCellRef,
    onCellLayout,
  } = useDeliveryCheckScrollView({
    minPos: pageMetrics.headerHeight - pageMetrics.offset,
    maxPos: windowDimensions.height - safeAreaInsets.bottom,
  });

  const onInvalidItem: InvalidDeliveryCheckItemHandler = useCallback(
    ({ item, index, message }) => {
      scrollToItemAnimated({ key: item._key, index }, () => {
        void showAlert('提示', message);
      });
    },
    [scrollToItemAnimated],
  );

  const onNewWorkItems = useCallback(
    (items: { key: string; index: number }[]) => {
      if (!items.length) return;
      scrollToItemAnimated({ key: items[0].key, index: items[0].index });
    },
    [scrollToItemAnimated],
  );

  const {
    pageTitle,
    staged,
    errorItemKey,
    changed$,
    activeReworkRecord,
    onRemarkTextChange,
    onOptionCheckChange,
    onRemove,
    onSave,
    onConfirm,
    onEditUnacceptedReason,
    onViewReworkRecords,
    onEditItemReworkRecord,
    onRemoveItemReworkRecord,
    onCancelEditItemRewordRecord,
    onFinishEditItemReworkRecord,
  } = useDeliveryCheck({ onInvalidItem, onNewWorkItems });

  const {
    currentIndex,
    prevIndex,
    wizardCellRef,
    onPrev,
    onNext,
    onSkip,
    onShowAll,
  } = useDeliveryCheckWizard();

  const {
    isUserPickerVisible,
    onUserPickerDismiss,
    onUserSelected,
    onUserPickerPress,
  } = useDeliveryCheckUserPicker();

  const {
    onTakeMedia,
    onEditMedia,
    onDeleteMedia,
    onPreviewVideo,
    onRetryMediaUpload,
  } = useDeliveryCheckMediaController({ changed$ });

  const { mode } = staged;

  const rightOptions = useCallback(() => {
    if (mode === 'wizard') {
      return (
        <TouchableOpacity onPress={onShowAll}>
          <Typography.Label light color="#fff" size={rpx(14)}>
            显示全部
          </Typography.Label>
        </TouchableOpacity>
      );
    } else {
      return (
        <TouchableOpacity onPress={onViewReworkRecords}>
          <Typography.Label medium color="#fff" size={rpx(16)}>
            内返记录
          </Typography.Label>
        </TouchableOpacity>
      );
    }
  }, [mode, onShowAll, onViewReworkRecords, rpx]);

  return (
    <>
      <WiredTaskDetailPageContainer
        title={pageTitle}
        scrollY={scrollOffset}
        navBarRightOptions={rightOptions}
        scrollViewRef={scrollViewRef}
        scrollViewForTablet
        footer={
          mode === 'list' ? (
            <BottomBar
              technicianName={staged.technicianName}
              onUserPickerPress={onUserPickerPress}
              onSave={onSave}
              onConfirm={onConfirm}
            />
          ) : null
        }
        contentContainerStyle={
          mode === 'wizard'
            ? undefined
            : {
                paddingBottom: safeAreaInsets.bottom + rpx(44),
              }
        }
      >
        {mode === 'wizard' ? (
          <>
            {staged.items[currentIndex] != null && (
              <DeliveryCheckCell
                ref={wizardCellRef}
                item={staged.items[currentIndex]}
                mode={mode}
                index={currentIndex}
                prevIndex={prevIndex.current}
                total={staged.items.length}
                onTakeMedia={onTakeMedia}
                onDeleteMedia={onDeleteMedia}
                onEditMedia={onEditMedia}
                onRetryMediaUpload={onRetryMediaUpload}
                onPreviewVideo={onPreviewVideo}
                onRemarkChange={onRemarkTextChange}
                onOptionCheckChange={onOptionCheckChange}
                onRemove={onRemove}
                onEditUnacceptedReason={onEditUnacceptedReason}
                onEditItemReworkRecord={onEditItemReworkRecord}
                onRemoveItemReworkRecord={onRemoveItemReworkRecord}
              />
            )}
            <TouchableOpacity
              onPress={onSkip}
              css={`
                align-self: flex-end;
                margin-top: ${rpx(10)}px;
              `}
            >
              <Typography.Label light color="#888" size={rpx(15)}>
                {`跳过 >`}
              </Typography.Label>
            </TouchableOpacity>
          </>
        ) : (
          staged.items.map((item, index) => (
            <DeliveryCheckCell
              key={item._key}
              item={item}
              mode={mode}
              compact={true}
              index={index}
              ref={ref => onCellRef(item._key, ref)}
              showErrorIndicators={errorItemKey === item._key}
              total={staged.items.length}
              onTakeMedia={onTakeMedia}
              onDeleteMedia={onDeleteMedia}
              onEditMedia={onEditMedia}
              onRetryMediaUpload={onRetryMediaUpload}
              onPreviewVideo={onPreviewVideo}
              onRemarkChange={onRemarkTextChange}
              onOptionCheckChange={onOptionCheckChange}
              onLayout={onCellLayout}
              onRemove={onRemove}
              onEditUnacceptedReason={onEditUnacceptedReason}
              onEditItemReworkRecord={onEditItemReworkRecord}
              onRemoveItemReworkRecord={onRemoveItemReworkRecord}
              css={`
                margin-bottom: ${rpx(10)}px;
                padding-top: ${rpx(20)}px;
                padding-bottom: ${rpx(15)}px;
              `}
            />
          ))
        )}
      </WiredTaskDetailPageContainer>

      {mode === 'wizard' ? (
        <BottomAction
          scrollY={scrollOffset}
          containerStyle={{
            justifyContent: mode === 'wizard' ? 'space-between' : 'center',
            paddingLeft: rpx(30),
            paddingRight: rpx(30),
          }}
        >
          {mode === 'wizard' ? (
            <>
              <BottomAction.Button
                variant="default"
                text="后退"
                icon={
                  <Icon
                    name="arrow-thin-left"
                    size={rpx(20)}
                    color="#000"
                    css={`
                      margin-right: ${rpx(5)}px;
                    `}
                  />
                }
                onPress={onPrev}
              />
              <BottomAction.Button
                variant="primary"
                text="下一步"
                iconRight={
                  <Icon
                    name="arrow-thin-right"
                    size={rpx(20)}
                    color="#fff"
                    css={`
                      margin-left: ${rpx(5)}px;
                    `}
                  />
                }
                onPress={onNext}
              />
            </>
          ) : (
            <>
              <BottomAction.Button
                variant="default"
                text="保存"
                onPress={onSave}
              />
              <BottomAction.Button
                variant="primary"
                text="预览报告"
                onPress={onConfirm}
                css={`
                  margin-left: ${rpx(10)}px;
                `}
              />
            </>
          )}
        </BottomAction>
      ) : null}

      <DeliveryCheckUserBottomSheetModal
        visible={isUserPickerVisible}
        onDismiss={onUserPickerDismiss}
        userId={staged.technicianId}
        onSelect={onUserSelected}
      />

      <ReworkRecordBottomSheet
        show={activeReworkRecord != null}
        recordInfo={activeReworkRecord}
        onDismiss={onCancelEditItemRewordRecord}
        onDone={onFinishEditItemReworkRecord}
      />
    </>
  );
});

const BottomBar = memo(
  ({
    technicianName,
    onUserPickerPress,
    onSave,
    onConfirm,
  }: {
    technicianName: string;
    onUserPickerPress: () => void;
    onSave: () => Promise<void>;
    onConfirm: () => Promise<void>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <StackLayout
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={rpx(15)}
      >
        <TouchableOpacity
          onPress={onUserPickerPress}
          css={`
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
          `}
        >
          <Typography.Label medium color="#000" size={rpx(16)}>
            {`质检员: ${technicianName}`}
          </Typography.Label>
          <Icon
            name="note"
            size={rpx(16)}
            css={`
              margin-left: ${rpx(5)}px;
              top: ${rpx(1.5)}px;
            `}
          />
        </TouchableOpacity>
        <View
          css={`
            flex: 1;
          `}
        />
        <LinkButton
          text="暂存检验结果"
          onPress={onSave}
          textStyle={{ fontSize: rpx(16) }}
        />
        <Center.Touchable
          onPress={onConfirm}
          css={`
            background-color: ${StatusColors.Info};
            height: ${rpx(44)}px;
            border-radius: ${rpx(44)}px;
            padding: 0 ${rpx(20)}px;
          `}
        >
          <Typography.Text color="#fff" bold size={rpx(18)}>
            预览报告
          </Typography.Text>
        </Center.Touchable>
      </StackLayout>
    );
  },
);
